import { useEffect, useState } from "react";
import { Pencil, X, Check } from "lucide-react";
import { membershipApi } from "../api";
import { MEMBERSHIP_PERMISSIONS } from "../permissions";
import type { IctAccessStatus, MemberIctAccess } from "../types";
import { MembershipAccess } from "./MembershipAccess";
import { MembershipStatusBadge } from "./MembershipStatusBadge";

const inp =
  "w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100";

function fmtDate(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}

interface IctForm {
  accessStatus: IctAccessStatus;
  portalAccountCreated: boolean;
  auctionAccessCreated: boolean;
  username: string;
  active: boolean;
  ictNotes: string;
}

export function MembershipIctPanel({
  memberId,
  access,
  onReload,
}: {
  memberId: number;
  access?: MemberIctAccess;
  onReload: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<IctForm>({
    accessStatus: "NOT_STARTED",
    portalAccountCreated: false,
    auctionAccessCreated: false,
    username: "",
    active: true,
    ictNotes: "",
  });

  useEffect(() => {
    if (access && editing) {
      setForm({
        accessStatus: access.accessStatus ?? "NOT_STARTED",
        portalAccountCreated: access.portalAccountCreated ?? false,
        auctionAccessCreated: access.auctionAccessCreated ?? false,
        username: access.username ?? "",
        active: access.active ?? true,
        ictNotes: access.ictNotes ?? "",
      });
    }
  }, [access, editing]);

  const set = <K extends keyof IctForm>(k: K, v: IctForm[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await membershipApi.updateIctAccess(memberId, form);
      setEditing(false);
      onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-black text-slate-900">ICT Access</h2>
        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.ICT_UPDATE]}>
          {!editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-50"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button type="button" onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-50">
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
              <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-2xl bg-emerald-700 px-3 py-2 text-xs font-black text-white hover:bg-emerald-800 disabled:opacity-60">
                <Check className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </MembershipAccess>
      </div>

      {editing ? (
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <label className="label">Access Status</label>
            <select value={form.accessStatus} onChange={(e) => set("accessStatus", e.target.value as IctAccessStatus)} className={inp}>
              <option value="NOT_STARTED">Not Started</option>
              <option value="ACCOUNT_PENDING">Account Pending</option>
              <option value="ACCOUNT_CREATED">Account Created</option>
              <option value="ACCESS_GRANTED">Access Granted</option>
              <option value="TRAINING_PENDING">Training Pending</option>
              <option value="COMPLETED">Completed</option>
              <option value="DEACTIVATED">Deactivated</option>
            </select>
          </div>
          <div>
            <label className="label">System Username</label>
            <input value={form.username} onChange={(e) => set("username", e.target.value)} placeholder="e.g. jdoe@eatta" className={inp} />
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 px-3 py-2.5">
              <input type="checkbox" checked={form.portalAccountCreated} onChange={(e) => set("portalAccountCreated", e.target.checked)} className="accent-emerald-700" />
              <span className="text-sm font-bold text-slate-700">Portal account created</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 px-3 py-2.5">
              <input type="checkbox" checked={form.auctionAccessCreated} onChange={(e) => set("auctionAccessCreated", e.target.checked)} className="accent-emerald-700" />
              <span className="text-sm font-bold text-slate-700">Auction access created</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 px-3 py-2.5">
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-emerald-700" />
              <span className="text-sm font-bold text-slate-700">Active (uncheck to deactivate)</span>
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="label">ICT Notes</label>
            <textarea value={form.ictNotes} onChange={(e) => set("ictNotes", e.target.value)} rows={3} placeholder="Any notes about ICT setup…" className={inp} />
          </div>
        </div>
      ) : (
        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <InfoCell label="Access Status" value={<MembershipStatusBadge value={access?.accessStatus ?? "NOT_STARTED"} />} />
          <InfoCell label="Portal Account" value={access?.portalAccountCreated ? "✓ Created" : "Pending"} accent={access?.portalAccountCreated} />
          <InfoCell label="Auction Access" value={access?.auctionAccessCreated ? "✓ Created" : "Pending"} accent={access?.auctionAccessCreated} />
          <InfoCell label="Username" value={access?.username ?? "—"} />
          <InfoCell label="Account Created" value={fmtDate(access?.accountCreatedAt)} />
          <InfoCell label="Created By" value={access?.accountCreatedBy ?? "—"} />
          <InfoCell label="Active" value={access?.active ? "Yes" : "No"} />
          <InfoCell label="Deactivated At" value={fmtDate(access?.deactivatedAt)} />
          <InfoCell label="Deactivated By" value={access?.deactivatedBy ?? "—"} />
          {access?.ictNotes && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:col-span-2 xl:col-span-3">
              <p className="text-xs font-black uppercase tracking-wide text-slate-400">ICT Notes</p>
              <p className="mt-1 text-sm text-slate-700">{access.ictNotes}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function InfoCell({ label, value, accent }: { label: string; value: React.ReactNode; accent?: boolean }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
      <p className="text-xs font-black uppercase tracking-wide text-slate-400">{label}</p>
      <div className="mt-1 font-bold text-slate-900">{value}</div>
    </div>
  );
}
